import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Globe, Mail, Phone, MapPin } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";

const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  website: Globe,
};

const footerPhoneDetails = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const localNumber = digits.length > 10 ? digits.slice(-10) : digits;
  const display = localNumber
    ? `+91 ${localNumber.slice(0, 5)} ${localNumber.slice(5)}`.trim()
    : "";
  return { display, tel: localNumber ? `+91${localNumber}` : "" };
};

const Footer = () => {
  const { logo_url, logo_alt, tagline, contact_mobile, contact_email, address, socials } = useBranding();
  const [pages, setPages] = useState([]);
  const [logoBroken, setLogoBroken] = useState(false);
  useEffect(() => { setLogoBroken(false); }, [logo_url]);

  useEffect(() => {
    api.get("/cms/pages").then((r) => setPages(r.data || [])).catch(() => {});
  }, []);

  const enabledSocials = Object.entries(socials || {}).filter(([, v]) => v?.enabled && v?.url);
  const phone = footerPhoneDetails(contact_mobile);

  return (
    <footer className="bg-[#1E2A24] text-white mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
            className="flex items-center gap-2 mb-4 w-fit"
            data-testid="footer-logo-link"
          >
            {logo_url && !logoBroken ? (
              <img
                src={logo_url}
                alt={logo_alt || "Eira"}
                className="h-12 w-auto max-w-[180px] object-contain"
                onError={() => setLogoBroken(true)}
                data-testid="footer-logo-img"
              />
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-[#C86D42] flex items-center justify-center">
                  <span className="font-serif text-white text-xl italic">E</span>
                </div>
                <span className="font-serif text-2xl">{logo_alt || "Eira"}</span>
              </>
            )}
          </Link>
          <p className="text-sm text-white/70 leading-relaxed">
            {tagline || "Pure. Essential. Everyday."}<br />
            A curated marketplace of natural Indian household, kitchen and wellness essentials.
          </p>
          {enabledSocials.length > 0 && (
            <div className="flex gap-3 mt-5" data-testid="footer-socials">
              {enabledSocials.map(([key, value]) => {
                const Icon = socialIcons[key] || Globe;
                return (
                  <a
                    key={key}
                    href={value.url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full bg-white/10 hover:bg-[#C86D42]"
                    data-testid={`footer-social-${key}`}
                    aria-label={key}
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-[#D99B26] mb-4">Shop</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/corporate-gifts" data-testid="footer-corporate-gifts">Corporate Gifts</Link></li>
            <li><Link to="/shop?category=household">Household</Link></li>
            <li><Link to="/shop?category=kitchen">Kitchen</Link></li>
            <li><Link to="/shop?category=personal-care">Personal Care</Link></li>
            <li><Link to="/shop?category=fashion">Fashion</Link></li>
            <li><Link to="/shop?category=food">Food</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-[#D99B26] mb-4">Support</div>
          <ul className="space-y-2 text-sm text-white/80" data-testid="footer-cms-links">
            <li>
              <Link
                to="/privacy-policy"
                className="hover:text-[#C86D42]"
                data-testid="footer-privacy-policy-link"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms-and-conditions"
                className="hover:text-[#C86D42]"
                data-testid="footer-terms-and-conditions-link"
              >
                Terms &amp; Conditions
              </Link>
            </li>
            {pages.length === 0 && <li className="text-white/40">No pages published yet</li>}
            {pages.map((p) => (
              <li key={p.slug}>
                <Link to={`/page/${p.slug}`} className="hover:text-[#C86D42]" data-testid={`footer-page-${p.slug}`}>{p.title}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-[#D99B26] mb-4">Get in Touch</div>
          <ul className="space-y-2 text-sm text-white/80" data-testid="footer-contact">
            {contact_mobile && (
              <li className="flex items-start gap-2">
                <Phone size={14} className="mt-0.5 flex-shrink-0" />
                <a
                  href={`tel:${phone.tel}`}
                  className="hover:text-[#C86D42]"
                  data-testid="footer-contact-phone"
                >
                  {phone.display}
                </a>
              </li>
            )}
            {contact_email && (
              <li className="flex items-start gap-2"><Mail size={14} className="mt-0.5 flex-shrink-0" /><a href={`mailto:${contact_email}`} className="hover:text-[#C86D42] whitespace-nowrap">{contact_email}</a></li>
            )}
            {address && (
              <li className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 flex-shrink-0" /><span className="whitespace-pre-line">{address}</span></li>
            )}
            {!contact_mobile && !contact_email && !address && (
              <li className="text-white/40">Add contact details in Admin → Settings</li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">
        © 2026 Eira. Handcrafted for conscious living.
      </div>
    </footer>
  );
};

export default Footer;
